import type { AuthResult, NestedAppAuthExecutor, TokenRequest } from 'owa-nested-app-auth-types';
import { isGlobalFeatureEnabled } from 'owa-feature-flags';
import { IMMERSIVEBIZCHAT_CLIENT_ID } from './constants';
import { logStartCoreUsage } from 'owa-analytics-start';
import { isBridgeError } from 'owa-nested-app-auth-types/lib/msal-types/BridgeError';

export interface PrefetchableNestedAppAuthExecutor extends NestedAppAuthExecutor {
    prefetchToken: (request: TokenRequest, requestHost: string, scenarioName: string) => void;
}

export interface PrefetchableMixin {
    prefetchToken: (request: TokenRequest, requestHost: string, scenarioName: string) => void;
}

type PrefetchedResponse = {
    responsePromise: Promise<AuthResult>;
    requestHost: string;
    clearTimeout?: () => void;
    completedAt?: number;

    // For telemetry
    scenarioName: string;
    correlationId: string;
    startedAt: number;
};

// Max amount of time unredeemed prefetches are kept in cache
const PREFETCH_EXPIRATION_SECONDS = 60 * 60; // 60 minutes

// Amount of time before a token expires that we consider it valid for prefetch redemption
const TOKEN_EXPIRY_GRACE_SECONDS = 2 * 60; // 2 minutes

function shouldRequestUsePrefetchCache(request: TokenRequest): boolean {
    return (
        isGlobalFeatureEnabled('auth-naa-prefetch') &&
        [IMMERSIVEBIZCHAT_CLIENT_ID].includes(request.clientId)
    );
}

function getRequestCacheKey(request: TokenRequest, requestHost: string): string {
    return [requestHost, request.clientId, request.scope, request.claims].join('|');
}

type MixinConstructor = new (...args: any[]) => NestedAppAuthExecutor;
type MixinReturnConstructor = new (...args: any[]) => PrefetchableNestedAppAuthExecutor;
export function Prefetchable<TBase extends MixinConstructor>(Base: TBase): MixinReturnConstructor {
    return class Mixin extends Base {
        private prefetchedResponses = new Map<string, PrefetchedResponse>();

        public prefetchToken(
            request: TokenRequest,
            requestHost: string,
            scenarioName: string
        ): void {
            if (!shouldRequestUsePrefetchCache(request)) {
                return;
            }

            const cacheKey = getRequestCacheKey(request, requestHost);
            const prefetchRequest = this.prefetchedResponses.get(cacheKey);

            if (prefetchRequest) {
                return;
            }

            // Access mailboxInfo from the base executor class - this is the Data-Context Mailbox (DCM)
            // for the account this executor was created for, ensuring proper telemetry routing for
            // multi-account/sovereign scenarios.
            const mailboxInfo = this.mailboxInfo;

            try {
                // start the token request
                const prefetchedResponse: PrefetchedResponse = {
                    startedAt: Date.now(),
                    responsePromise: super.getTokenRequest(request, requestHost),
                    requestHost,
                    correlationId: request.correlationId,
                    scenarioName,
                };

                this.prefetchedResponses.set(cacheKey, prefetchedResponse);

                void prefetchedResponse.responsePromise
                    .then(result => {
                        if (this.prefetchedResponses.get(cacheKey) === prefetchedResponse) {
                            const completedAt = Date.now();
                            prefetchedResponse.completedAt = completedAt;

                            if (result.token.expires_in <= TOKEN_EXPIRY_GRACE_SECONDS) {
                                // token expires too soon, do not bother caching it
                                this.prefetchedResponses.delete(cacheKey);

                                logStartCoreUsage('NAAPrefetchOutcome', mailboxInfo, {
                                    scenarioName,
                                    prefetchCorrelationId: request.correlationId,
                                    aborted: true,
                                    timeInCache: Date.now() - prefetchedResponse.startedAt,
                                    expiresIn: result.token.expires_in,
                                    protocolMode: this.getProtocolMode?.(),
                                });
                                return;
                            }

                            // on successful token acquisition, set up a timer to clean out unredeemed prefetches
                            const cleanupSeconds = Math.min(
                                PREFETCH_EXPIRATION_SECONDS,
                                result.token.expires_in - TOKEN_EXPIRY_GRACE_SECONDS
                            );
                            const timerId = setTimeout(() => {
                                if (this.prefetchedResponses.get(cacheKey) === prefetchedResponse) {
                                    this.prefetchedResponses.delete(cacheKey);

                                    logStartCoreUsage('NAAPrefetchOutcome', mailboxInfo, {
                                        scenarioName,
                                        prefetchCorrelationId: request.correlationId,
                                        expired: true,
                                        requestDuration: completedAt - prefetchedResponse.startedAt,
                                        timeInCache: Date.now() - prefetchedResponse.startedAt,
                                        expiresIn: result.token.expires_in,
                                        protocolMode: this.getProtocolMode?.(),
                                    });
                                }
                            }, cleanupSeconds * 1000);
                            prefetchedResponse.clearTimeout = () => clearTimeout(timerId);
                        }
                    })
                    .catch(error => {
                        // on error, clean up the prefetch cache immediately, no need to store errors
                        if (this.prefetchedResponses.get(cacheKey) === prefetchedResponse) {
                            this.prefetchedResponses.delete(cacheKey);

                            logStartCoreUsage('NAAPrefetchOutcome', mailboxInfo, {
                                scenarioName,
                                prefetchCorrelationId: request.correlationId,
                                errored: true,
                                bridgeError: isBridgeError(error) ? error.status : undefined,
                                timeInCache: Date.now() - prefetchedResponse.startedAt,
                                protocolMode: this.getProtocolMode?.(),
                            });
                        }
                    });
            } catch {}
        }

        public override async getTokenRequest(
            request: TokenRequest,
            host: string,
            skipBrokerClaims?: boolean
        ): Promise<AuthResult> {
            if (shouldRequestUsePrefetchCache(request)) {
                const cacheKey = getRequestCacheKey(request, host);
                const prefetchedResponse = this.prefetchedResponses.get(cacheKey);

                if (prefetchedResponse) {
                    // Access mailboxInfo from the base executor class - this is the Data-Context Mailbox (DCM)
                    // for the account this executor was created for, ensuring proper telemetry routing for
                    // multi-account/sovereign scenarios.
                    const mailboxInfo = this.mailboxInfo;

                    const promise = prefetchedResponse.responsePromise;
                    prefetchedResponse.clearTimeout?.();
                    this.prefetchedResponses.delete(cacheKey);

                    const commonLogProps: Record<string, string | boolean | number | undefined> = {
                        scenarioName: prefetchedResponse.scenarioName,
                        prefetchCorrelationId: prefetchedResponse.correlationId,
                        requestCorrelationId: request.correlationId,
                        redeemed: true,
                        timeInCache: Date.now() - prefetchedResponse.startedAt,
                        protocolMode: this.getProtocolMode?.(),
                    };

                    if (prefetchedResponse.completedAt !== undefined) {
                        const result = await promise;

                        // since expiresIn is a relative timestamp, not an absolute one, we need
                        // to adjust it and subtract the time between when it was fetched and now
                        result.token.expires_in -= Math.ceil(
                            (Date.now() - prefetchedResponse.completedAt) / 1000
                        );

                        // Only return the prefetched token if it still has enough lifetime.
                        // Browser timer throttling (backgrounded/frozen tabs) can delay the
                        // cleanup setTimeout, allowing stale entries to survive in the cache
                        // well past their expiry — which would produce a negative expires_in.
                        if (result.token.expires_in > TOKEN_EXPIRY_GRACE_SECONDS) {
                            logStartCoreUsage('NAAPrefetchOutcome', mailboxInfo, {
                                ...commonLogProps,
                                hasCompleted: true,
                                requestDuration:
                                    prefetchedResponse.completedAt - prefetchedResponse.startedAt,
                                expiresIn: result.token.expires_in,
                            });

                            return result;
                        }
                    } else {
                        logStartCoreUsage('NAAPrefetchOutcome', mailboxInfo, commonLogProps);
                        return promise;
                    }
                }
            }

            return super.getTokenRequest(request, host, skipBrokerClaims);
        }
    };
}
